import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import gsap from 'gsap';

import pathToModel from '../../assets/models/stylised_character.glb';
import pathToTexture from '../../assets/img/textures/theMofGame1.png';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/f8ffe5-06d6a0-1b9aaa-ef476f-ffc43d');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.isAnimating = false;

        //this.scene.background = this.colourArr[4]; 
        this.scene.fog = new THREE.Fog(this.colourArr[0], 10, 100);

        this.setupCamera();
        this.setupLight();
        this.loadModels();
        this.setupControls();
        this.setupGeometry();
    }

    setupCamera() {
        let VIEW_ANGLE = 50;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 100

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.z = 40;
        this.camera.position.y = 20;
        this.camera.position.x = -20;
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    setupLight() {
        let light = new THREE.HemisphereLight(this.colourArr[0], this.colourArr[1], 1);
        light.position.set(0, 0.5, -1);
        this.scene.add(light);

        let dirLight = new THREE.DirectionalLight(this.colourArr[0], 3);
        dirLight.position.set(0, 0.5, 1);
        this.scene.add(dirLight);

        this.light = dirLight;
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        // this.controls.autoRotateSpeed = .9;
        // this.controls.autoRotate = true;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = false;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 80;
        this.controls.minDistance = 5;
        //this.controls.addEventListener('change', this.update.bind(this));
    }

    setupShaderMaterial() {
        const texture = new THREE.TextureLoader().load(pathToTexture);

        //get the matrices from camera
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();
        this.camera.updateWorldMatrix();
        const viewMatrixCamera = this.camera.matrixWorldInverse.clone();
        const projectionMatrixCamera = this.camera.projectionMatrix.clone();
        let projPosition = this.camera.position;//.clone();

        this.uniforms = {
            uTexture0: { value: texture },
            uViewMatrixCamera: { value: viewMatrixCamera },
            uProjectionMatrixCamera: { value: projectionMatrixCamera },
            uProjPosition: { value: projPosition },
            uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            uTime: { value: 0 },
            uLightPos: { value: this.light.position },
            uAmbientLightColor: { value: this.colourArr[3] },
            uSpecularColor: { value: this.colourArr[0] },
            uDiffLightColor: { value: this.colourArr[1] },
            uStripeSize: { value: 0.9 }
        };

        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                precision mediump float;

                uniform mat4 uViewMatrixCamera;
                uniform mat4 uProjectionMatrixCamera;
                uniform vec3 uLightPos;
                uniform vec3 uAmbientLightColor;
                uniform vec3 uSpecLightColor;
                uniform vec3 uDiffLightColor;

                varying vec3 vNormal;
                varying vec2 vUv; 
                varying vec3 viewDir;
                varying vec4 vColor;
                varying vec4 vTexCoords;
                varying vec4 vWorldPosition;

                const float shininess = 5.0;

                vec4 computeLight(
                    vec3 uAmbLightCol,
                    vec3 uDifLightCol,
                    vec3 uSpecLightCol,
                    vec3 nViewDir,
                    vec3 nLightDir,
                    vec3 norm,
                    float shin)
                {
                    vec4 ambientLight = vec4(uAmbLightCol, 1.0);
                    vec4 diffuseLight = vec4(max(dot(nLightDir, norm), 0.0) * uDifLightCol, 1.0);
                    vec3 refl = reflect(-nLightDir, norm);
                    vec4 specularLight = vec4(uSpecLightCol, 1.0);
                    if(dot(nLightDir, nViewDir) > 0.0) 
                    {
                        specularLight = vec4(pow(min(dot(refl, nViewDir), 0.0), shin) * uSpecLightColor, 1.0);
                    }
                    vec4 light = ambientLight + diffuseLight + specularLight;
                    return light;
                }
            
                void main()
                {
                    vNormal = normalize(normalMatrix * normal);
                    //vNormal = normal;
                    vUv = uv;

                    //custom texture projection (see: vTexCoords modif)
                    vWorldPosition = modelMatrix * vec4(position, 1.0);
                    vTexCoords = uProjectionMatrixCamera *  modelViewMatrix * vec4(position, 1.0); //uViewMatrixCamera * vWorldPosition;

                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

                    //vertex lighting
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.);
                    vec3 viewPos = -mvPosition.xyz;
                    vec3 normViewDir = normalize(viewPos);
                    viewDir = normViewDir;
                    vec4 lightDir = viewMatrix * vec4(uLightPos, 0.0);
                    vec3 normLightDir = normalize(lightDir.xyz); //normalize(uLightPos - viewPos);
                    vColor = computeLight(
                                        uAmbientLightColor,
                                        uDiffLightColor,
                                        uSpecLightColor,
                                        normViewDir,
                                        normLightDir,
                                        vNormal,
                                        shininess);
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform sampler2D uTexture0;
                uniform vec3 uProjPosition;
                uniform float uTime;
                uniform vec2 uResolution;
                uniform vec3 uAmbientLightColor;
                uniform vec3 uDiffLightColor;
                uniform float uStripeSize;

                varying vec4 vWorldPosition;
                varying vec4 vTexCoords;
                varying vec2 vUv;
                varying vec3 vNormal;
                varying vec3 viewDir;
                varying vec4 vColor;

                void main()
                {
                    //texture projection
                    vec2 uv = (vTexCoords.xy / vTexCoords.w) * 0.5 + 0.5;
                    vec4 outColor = texture2D(uTexture0, uv); //vUv
                    vec3 projectorDirection = normalize(uProjPosition - vWorldPosition.xyz);
                    float dotProduct = dot(vNormal, projectorDirection);
                    if(dotProduct < 0.0) outColor = vColor;
                    
                    //regular stuff
                    vec2 pos = vUv * (gl_FragCoord.xy/uResolution);
                    vec3 col0 = uAmbientLightColor;
                    vec3 col1 = uDiffLightColor;

                    //rim lighting
                    float rim = max(dot(viewDir, vNormal), 0.);

                    //texture proj + rim:
                    float stripeChange = uStripeSize * sin(uTime);
                    vec3 emission = fract(pos.y * 10.) > stripeChange ? col0 * rim : col1 * rim; //col0 * rim;
                    gl_FragColor = vec4(outColor.rgb - emission, 1.0);
                }
            `
        });
        return material;
    }

    setupShaderMaterialTwo() {
        const texture = new THREE.TextureLoader().load(pathToTexture);
        //texture.anisotropy = 72;

        //get the matrices from camera
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();
        this.camera.updateWorldMatrix();
        const viewMatrixCamera = this.camera.matrixWorldInverse.clone();
        const projectionMatrixCamera = this.camera.projectionMatrix.clone();
        let projPosition = this.camera.position;//.clone();

        this.uniforms = {
            uTexture0: { value: texture },
            uViewMatrixCamera: { value: viewMatrixCamera },
            uProjectionMatrixCamera: { value: projectionMatrixCamera },
            uProjPosition: { value: projPosition },
            uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            uTime: { value: 0 },
            uLightPos: { value: this.light.position },
            uAmbientLightColor: { value: this.colourArr[3] },
            uSpecularColor: { value: this.colourArr[0] },
            uDiffLightColor: { value: this.colourArr[1] },
            uStripeSize: { value: 0.9 }
        };

        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                precision mediump float;

                uniform mat4 uViewMatrixCamera;
                uniform mat4 uProjectionMatrixCamera;

                varying vec3 vNormal;
                varying vec2 vUv; 
                varying vec3 vViewDir;
                varying vec4 vTexCoords;
                varying vec4 vWorldPosition;
            
                void main()
                {
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.);
                    vViewDir = -mvPosition.xyz;
                    vNormal = normalize(normalMatrix * normal);
                    vUv = uv;
                    vWorldPosition = modelMatrix * vec4(position, 1.0);
                    vTexCoords = uProjectionMatrixCamera *  modelViewMatrix * vec4(position, 1.0); //uViewMatrixCamera * vWorldPosition;

                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }    
            `,
            fragmentShader: `
                precision mediump float;

                uniform sampler2D uTexture0;
                uniform vec3 uProjPosition;
                uniform float uTime;
                uniform vec2 uResolution;
                uniform vec3 uAmbientLightColor;
                uniform vec3 uDiffLightColor;
                uniform vec3 uSpecLightColor;
                uniform vec3 uLightPos;
                uniform float uStripeSize;

                varying vec4 vWorldPosition;
                varying vec4 vTexCoords;
                varying vec2 vUv;
                varying vec3 vNormal;
                varying vec3 vViewDir;

                const float shininess = 5.0;

                vec4 computeLight(
                    vec3 uAmbLightCol,
                    vec3 uDifLightCol,
                    vec3 uSpecLightCol,
                    vec3 nViewDir,
                    vec3 nLightDir,
                    vec3 norm,
                    float shin)
                {
                    vec4 ambientLight = vec4(uAmbLightCol, 1.0);
                    vec4 diffuseLight = vec4(max(dot(nLightDir, norm), 0.0) * uDifLightCol, 1.0);
                    vec3 refl = reflect(-nLightDir, norm);
                    vec4 specularLight = vec4(uSpecLightCol, 1.0);
                    if(dot(nLightDir, nViewDir) > 0.0) 
                    {
                        specularLight = vec4(pow(min(dot(refl, nViewDir), 0.0), shin) * uSpecLightColor, 1.0);
                    }
                    vec4 light = ambientLight + diffuseLight + specularLight;
                    return light;
                }

                void main()
                {
                    //light
                    vec4 lDir = viewMatrix * vec4(uLightPos, 0.0);
                    vec3 nLightDir = normalize(lDir.xyz);
                    vec3 nViewDir = normalize(vViewDir);
                    vec3 n = normalize(vNormal);

                    vec4 light = computeLight(
                            uAmbientLightColor,
                            uDiffLightColor,
                            uSpecLightColor,
                            nViewDir,
                            nLightDir,
                            n,
                            shininess);

                    //texture projection
                    vec2 uv = (vTexCoords.xy / vTexCoords.w) * 0.5 + 0.5;
                    vec4 outColor = texture2D(uTexture0, uv) * light; //vUv
                    vec3 projectorDirection = normalize(uProjPosition - vWorldPosition.xyz);
                    float dotProduct = dot(n, projectorDirection);
                    if(dotProduct < 0.0) outColor = light;
                    
                    //regular stuff
                    vec2 pos = vUv * (gl_FragCoord.xy/uResolution);
                    vec3 col0 = uAmbientLightColor;
                    vec3 col1 = uDiffLightColor;

                    //rim lighting
                    float rim = 0.8 - max(dot(nViewDir, n), 0.);

                    //texture proj + rim:
                    float stripeChange = uStripeSize * sin(uTime);
                    vec3 emission = fract(pos.y * 10.) > stripeChange ? col0 * rim : col1 * rim; //col0 * rim;
                    gl_FragColor = vec4(outColor.rgb - emission, 1.0);
                }
            `
        });
        return material;
    }

    loadModels() {
        let material = this.setupShaderMaterialTwo();
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, (gltf) => {
            this.root = gltf.scene;
            this.root.scale.set(10, 10, 10);
            this.root.position.set(0, -10, 0);
            this.root.traverse(node => {
                if (node.isMesh) {
                    node.material = material;
                }
            });
            this.scene.add(this.root);
        });
    }

    setupGeometry() {
        let num = 300;
        let geometry = new THREE.BoxBufferGeometry(0.05);
        let geo2 = new THREE.SphereBufferGeometry(getRandom(0.1, 0.5), 32, 32);
        let material = this.setupShaderMaterialTwo();
        let material2 = this.setupShaderMaterial();
        let mesh1 = new THREE.Mesh(geometry, material);
        let mesh2 = new THREE.Mesh(geo2, material2);
        this.geoArray = [];
        for (let i = 0; i < num; i++) {
            // let mesh1 = new THREE.Mesh(geometry, material);
            // mesh.position.x = Math.random() * 20 - 5;
            // mesh.position.y = Math.random() * 20 - 5;
            // mesh.position.z = Math.random() * 20 - 5;
            // mesh.scale.x = mesh.scale.y = mesh.scale.z = Math.random() * 2 + 1;
            // this.geoArray.push(mesh);
            // this.geoArray.push(mesh2);
            // this.scene.add(mesh);

            const element = Math.random() > 0.5 ? mesh1.clone() : mesh2.clone();
            element.position.x = Math.random() * 40 - 20;
            element.position.y = Math.random() * 40 - 20;
            element.position.z = Math.random() * 20 - 10;
            element.scale.x = element.scale.y = element.scale.z = Math.random() * 2 + 1;
            this.geoArray.push(element);
            this.scene.add(element);
        }
    }

    orbitCamera(rotSpeed) {
        let x = this.camera.position.x;
        let z = this.camera.position.z;

        this.camera.position.x = x * Math.cos(rotSpeed) + z * Math.sin(rotSpeed);
        this.camera.position.z = z * Math.cos(rotSpeed) - x * Math.sin(rotSpeed);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    animate(mesh) {
        this.isAnimating = true;
        const originalPosX = mesh.position.x;
        const originalPosY = mesh.position.y;
        const originalPosZ = mesh.position.z;
        const easing = 'elastic';
        let tween = gsap.timeline();
        tween.to(mesh.position, 10, { x: getRandom(-20, 20), y: getRandom(-20, 20), z: getRandom(10, -10), ease: easing })
            .to(mesh.position, 10, { x: originalPosX, y: originalPosY, z: originalPosZ, ease: easing });
        tween.eventCallback("onComplete", () => {
            this.isAnimating = false;
        });
    }

    rotate(mesh, speed) {
        let tween = gsap
            .to(mesh.rotation, speed, { y: mesh.rotation.y + Math.PI * 0.5, z: mesh.rotation.z + Math.PI * 0.5 });
        tween.play();
    }

    update() {
        if (this.disposed) return;
        if (this.camera) this.orbitCamera(0.001);
        if (this.controls) this.controls.update();
        if (this.uniforms) this.uniforms['uTime'].value += 0.01;
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onMouseDown(e) {
        if (this.isDisposed) return;
        if (this.isAnimating) return;
        for (let i = 0; i < this.geoArray.length; i++) {
            this.animate(this.geoArray[i]);
            this.rotate(this.geoArray[i], 10);
        }
    }

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.material.dispose();
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    }
}

const getRandom = (min, max) => {
    return Math.random() * (max - min) + min;
}


